import React, {Children, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {getFromStore, StorePath} from '../../services/store.service';

type CarouselProps = {
	children: ReactNode;
	gap?: number;
	ariaLabel?: string;
};

const DESKTOP_TRACK_INSET = 36;

const carouselStyles = {
	wrapper: {
		position: 'relative',
	} as const,
	track: {
		display: 'flex',
		overflowX: 'auto',
		scrollSnapType: 'x mandatory',
		scrollBehavior: 'smooth',
		scrollbarWidth: 'none',
	} as const,
	item: {
		flex: '0 0 auto',
		scrollSnapAlign: 'start',
	} as const,
	button: {
		position: 'absolute',
		top: '50%',
		transform: 'translateY(-50%)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '28px',
		height: '28px',
		border: '1px solid #e5e7eb',
		borderRadius: '20px',
		backgroundColor: 'white',
		color: 'var(--primary-color)',
		opacity: 1,
		transition: '0.5s ease-in-out',
		fontSize: '18px',
		lineHeight: 1,
		zIndex: 1,
	} as const,
	prevButton: {
		left: 0,
	} as const,
	nextButton: {
		right: 0,
	} as const,
	disabledButton: {
		visibility: 'hidden',
		opacity: 0,
		pointerEvents: 'none',
	} as const,
};

const Carousel = ({children, gap = 60, ariaLabel = 'Carousel'}: CarouselProps) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);
	const params = getFromStore(StorePath.TRACK_PARAMS) as {is_mobile: boolean};
	const isMobile = params.is_mobile;
	const items = Children.toArray(children);

	const updateButtonState = useCallback(() => {
		const track = trackRef.current;
		if (!track || items.length <= 1) {
			setCanScrollPrev(false);
			setCanScrollNext(false);
			return;
		}

		const carouselItems = track.querySelectorAll<HTMLElement>('.carousel-item');
		const firstItem = carouselItems[0];
		const lastItem = carouselItems[carouselItems.length - 1];

		if (!firstItem || !lastItem) {
			setCanScrollPrev(false);
			setCanScrollNext(false);
			return;
		}

		const trackRect = track.getBoundingClientRect();
		const firstItemRect = firstItem.getBoundingClientRect();
		const lastItemRect = lastItem.getBoundingClientRect();
		const hasGeometry = trackRect.width > 0 && (firstItemRect.width > 0 || lastItemRect.width > 0);

		if (hasGeometry) {
			setCanScrollPrev(firstItemRect.left < trackRect.left - 1);
			setCanScrollNext(lastItemRect.right > trackRect.right + 1);
			return;
		}

		const {scrollLeft, clientWidth, scrollWidth} = track;
		setCanScrollPrev(scrollLeft > 1);
		setCanScrollNext(scrollLeft + clientWidth < scrollWidth - 1);
	}, [items.length]);

	useEffect(() => {
		updateButtonState();
		window.addEventListener('resize', updateButtonState);
		return () => {
			window.removeEventListener('resize', updateButtonState);
		};
	}, [updateButtonState]);

	useEffect(() => {
		const track = trackRef.current;
		if (!track) {
			return;
		}
		if (typeof track.scrollTo === 'function') {
			track.scrollTo({left: 0});
		} else {
			track.scrollLeft = 0;
		}
		updateButtonState();
	}, [items.length, updateButtonState]);

	const scrollByPage = (direction: 1 | -1) => {
		const track = trackRef.current;
		if (!track) {
			return;
		}

		const scrollAmount = direction * Math.max(track.clientWidth, 220 + gap);
		if (typeof track.scrollBy === 'function') {
			track.scrollBy({
				left: scrollAmount,
				behavior: 'smooth',
			});
		} else {
			track.scrollLeft += scrollAmount;
		}
		updateButtonState();
	};

	const trackInset = isMobile ? '0px' : `${DESKTOP_TRACK_INSET}px`;
	const trackMarginLeft = isMobile ? '64px' : '50px';

	return (
		<div className="carousel" style={carouselStyles.wrapper} aria-label={ariaLabel}>
			<button type="button"
				className={`carousel-button carousel-button-prev ${canScrollPrev ? 'cursor-pointer' : 'carousel-button-disabled'}`}
				style={{
					...carouselStyles.button,
					...carouselStyles.prevButton,
					left: isMobile ? 0 : 4,
					...(!canScrollPrev ? carouselStyles.disabledButton : {}),
				}}
				aria-label="Previous item"
				disabled={!canScrollPrev}
				onClick={() => scrollByPage(-1)}>
				&#x2039;
			</button>

			<div ref={trackRef}
				className="carousel-track scroll-x-hide"
				style={{...carouselStyles.track, gap: `${gap}px`, marginLeft: trackMarginLeft, paddingLeft: trackInset, paddingRight: trackInset}}
				onScroll={updateButtonState}>
				{items.map((item, index) => (
					<div className="carousel-item" style={carouselStyles.item} key={index}>
						{item}
					</div>
				))}
			</div>

			<button type="button"
				className={`carousel-button carousel-button-next ${canScrollNext ? 'cursor-pointer' : 'carousel-button-disabled'}`}
				style={{
					...carouselStyles.button,
					...carouselStyles.nextButton,
					right: isMobile ? 0 : 4,
					...(!canScrollNext ? carouselStyles.disabledButton : {}),
				}}
				aria-label="Next item"
				disabled={!canScrollNext}
				onClick={() => scrollByPage(1)}>
				&#x203A;
			</button>
		</div>
	);
};

export default Carousel;
